import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  Archive, BookOpenCheck, Check, CircleHelp, FileCheck2, Fingerprint,
  FileUp, MessageSquareText, Pencil, Plus, RotateCcw, Search, ShieldQuestion, Sparkles, Tags, X,
} from 'lucide-react';
import {FormEvent, useMemo, useState} from 'react';
import './CareerProfileWorkspacePage.css';
import {api} from '../api';
import {
  Modal, PrimaryButton, QuietButton, SecondaryButton, Select, Surface, Textarea, TextInput,
} from '../design';
import {ApplicationAnswer, ApprovedBullet, CareerFact, Persona} from '../types';
import {EvidenceCapture} from '../components/EvidenceCapture';
import {EmptyPanel, ErrorPanel, LoadingPanel, PageHeader, formatRelativeDate} from '../components/PagePrimitives';

type Tab = 'facts' | 'bullets' | 'personas' | 'answers';
type FactForm = Omit<CareerFact, 'id' | 'updatedAt' | 'confidence' | 'provenance'> & {
  id?: string; confidence: string; provenance: string;
};
type BulletForm = {
  id: string; text: string; tags: string; factIds: string[]; personaIds: string[]; wasApproved: boolean;
};
type PersonaForm = {
  id?: string; name: string; roleFamily: string; seniority: string; locationStrategy: string;
  compensationMinimum: string; queries: string; active: boolean;
};
type AnswerForm = Omit<ApplicationAnswer, 'id' | 'updatedAt'> & {id?: string};

const splitValues = (value: string): string[] =>
  [...new Set(value.split(/\r?\n|,/).map((item) => item.trim()).filter(Boolean))];
const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'The change could not be saved.';
const freshConfirmation = (value: ApplicationAnswer['sensitivity']): boolean =>
  ['attestation', 'signature', 'employer_consent'].includes(value);
const emptyFact = (): FactForm => ({
  type: '', value: '', state: 'user_confirmed', confidence: '1',
  sourceLabel: 'Candidate entry', provenance: 'direct-user', archived: false,
});
const emptyPersona = (): PersonaForm => ({
  name: '', roleFamily: '', seniority: '', locationStrategy: '',
  compensationMinimum: '', queries: '', active: true,
});
const emptyAnswer = (): AnswerForm => ({
  normalizedQuestion: '', originalQuestion: '', answer: '', sensitivity: 'none',
  provenance: 'direct_user', reusePreference: 'ask_each_time',
});

export const CareerProfilePage = () => {
  const client = useQueryClient();
  const [tab, setTab] = useState<Tab>('facts');
  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [factForm, setFactForm] = useState<FactForm | null>(null);
  const [bulletForm, setBulletForm] = useState<BulletForm | null>(null);
  const [personaForm, setPersonaForm] = useState<PersonaForm | null>(null);
  const [personaSuggestionStatus, setPersonaSuggestionStatus] = useState('');
  const [answerForm, setAnswerForm] = useState<AnswerForm | null>(null);
  const [notice, setNotice] = useState('');
  const [replacementResume, setReplacementResume] = useState<File | null>(null);

  const bootstrap = useQuery({queryKey: ['bootstrap'], queryFn: api.bootstrap});
  const documents = useQuery({queryKey: ['documents'], queryFn: api.documents});
  const facts = useQuery({queryKey: ['career-facts'], queryFn: api.facts});
  const bullets = useQuery({queryKey: ['career-bullets'], queryFn: api.bullets});
  const personas = useQuery({queryKey: ['personas'], queryFn: api.personas});
  const answers = useQuery({queryKey: ['application-answers'], queryFn: api.applicationAnswers});
  const refresh = (...keys: string[]) =>
    Promise.all(keys.map((key) => client.invalidateQueries({queryKey: [key]})));
  const replaceResume = useMutation({
    mutationFn: (file: File) => api.uploadResume(file),
    onSuccess: async () => {
      setReplacementResume(null);
      setNotice('The new source resume is active. Only dependent facts and generated documents were reopened for review.');
      await refresh('bootstrap', 'documents', 'career-facts', 'career-bullets', 'opportunities', 'action-items');
    },
  });

  const saveFact = useMutation({
    mutationFn: async (form: FactForm) => {
      const input = {
        type: form.type.trim(), value: form.value.trim(),
        state: form.state === 'source_backed' ? 'user_confirmed' as const : form.state,
        confidence: Number(form.confidence), sourceLabel: form.sourceLabel.trim(),
        provenance: splitValues(form.provenance), archived: form.archived,
      };
      return form.id ? api.updateFact(form.id, {...input, ...(form.reviewRequired ? {reviewRequired: false} : {})}) : api.createFact(input);
    },
    onSuccess: async () => { setFactForm(null); await refresh('career-facts'); },
  });
  const updateFact = useMutation({
    mutationFn: ({id, input}: {id: string; input: Partial<CareerFact>}) => api.updateFact(id, input),
    onSuccess: () => refresh('career-facts'),
  });
  const saveBullet = useMutation({
    mutationFn: (form: BulletForm) => api.updateBullet(form.id, {
      text: form.text.trim(), tags: splitValues(form.tags),
      factIds: form.factIds, personaIds: form.personaIds,
    }),
    onSuccess: async (_saved, form) => {
      setBulletForm(null);
      setNotice(form.wasApproved
        ? 'The approved wording was preserved. Your edit is a new draft version.'
        : 'Bullet changes saved.');
      await refresh('career-bullets');
    },
  });
  const updateBullet = useMutation({
    mutationFn: ({id, input}: {id: string; input: Partial<ApprovedBullet>}) => api.updateBullet(id, input),
    onSuccess: () => refresh('career-bullets'),
  });
  const savePersona = useMutation({
    mutationFn: async (form: PersonaForm) => {
      const input = {
        name: form.name.trim(), roleFamily: form.roleFamily.trim(),
        seniority: form.seniority.trim() || undefined,
        locationStrategy: form.locationStrategy.trim() || undefined,
        compensationMinimum: form.compensationMinimum ? Number(form.compensationMinimum) : undefined,
        queries: splitValues(form.queries), active: form.active, confirmed: false,
      };
      return form.id ? api.updatePersona(form.id, input) : api.createPersona(input);
    },
    onSuccess: async () => {
      setPersonaForm(null);
      setNotice('Persona saved as a draft. Confirm its search queries when they are ready.');
      await refresh('personas');
    },
  });
  const confirmPersona = useMutation({
    mutationFn: (id: string) => api.updatePersona(id, {confirmed: true}),
    onSuccess: async () => { setNotice('Search preferences confirmed.'); await refresh('personas'); },
  });
  const suggestPersonaQueries = useMutation({
    mutationFn: (form: PersonaForm) => api.suggestPersonaQueries({
      roleFamily: form.roleFamily.trim(),
      seniority: form.seniority.trim() || undefined,
      locationStrategy: form.locationStrategy.trim() || undefined,
      compensationMinimum: form.compensationMinimum ? Number(form.compensationMinimum) : undefined,
    }),
    onSuccess: (suggestions) => {
      setPersonaForm((current) => current ? {...current, queries: suggestions.queries.join('\n')} : current);
      const kinds = [...new Set(suggestions.basis.map((item) => item.kind))];
      setPersonaSuggestionStatus(
        `Drafted ${suggestions.queries.length} queries from ${kinds.join(', ')}. Review and save them; confirmation remains a separate step.`,
      );
    },
    onError: (error) => {
      setPersonaSuggestionStatus(errorMessage(error));
    },
  });
  const saveAnswer = useMutation({
    mutationFn: async (form: AnswerForm) => {
      const input = {
        normalizedQuestion: form.normalizedQuestion.trim(),
        originalQuestion: form.originalQuestion.trim(), answer: form.answer.trim(),
        sensitivity: form.sensitivity, provenance: form.provenance,
        reusePreference: freshConfirmation(form.sensitivity) ? 'never' as const : form.reusePreference,
        ...(form.sourceApplicationId ? {sourceApplicationId: form.sourceApplicationId} : {}),
        ...(form.sourcePageId ? {sourcePageId: form.sourcePageId} : {}),
      };
      return form.id ? api.updateApplicationAnswer(form.id, input) : api.createApplicationAnswer(input);
    },
    onSuccess: async () => { setAnswerForm(null); await refresh('application-answers'); },
  });

  const needle = query.trim().toLowerCase();
  const filteredFacts = useMemo(() => (facts.data?.items ?? []).filter((fact) =>
    (showArchived || !fact.archived) &&
    (!needle || (fact.value + ' ' + fact.type + ' ' + fact.sourceLabel).toLowerCase().includes(needle))),
  [facts.data, needle, showArchived]);
  const filteredBullets = useMemo(() => (bullets.data?.items ?? []).filter((bullet) =>
    (showArchived || bullet.state !== 'archived') &&
    (!needle || (bullet.text + ' ' + bullet.tags.join(' ')).toLowerCase().includes(needle))),
  [bullets.data, needle, showArchived]);
  const filteredPersonas = useMemo(() => (personas.data?.items ?? []).filter((persona) =>
    !needle || (persona.name + ' ' + persona.roleFamily + ' ' + persona.queries.join(' ')).toLowerCase().includes(needle)),
  [personas.data, needle]);
  const filteredAnswers = useMemo(() => (answers.data?.items ?? []).filter((answer) =>
    !needle || (answer.originalQuestion + ' ' + answer.normalizedQuestion + ' ' + answer.answer).toLowerCase().includes(needle)),
  [answers.data, needle]);
  const active = tab === 'facts' ? facts : tab === 'bullets' ? bullets : tab === 'personas' ? personas : answers;
  const sourceResumes = (documents.data?.items ?? []).filter((document) => document.kind === 'source_resume');
  const activeResumeId = bootstrap.data?.onboarding.resumeDocumentId;
  const activeResume = sourceResumes.find((document) => document.id === activeResumeId);
  const priorResumes = sourceResumes.filter((document) => document.id !== activeResumeId);

  const openFact = (fact?: CareerFact) => setFactForm(fact ? {
    ...fact, confidence: String(fact.confidence), provenance: fact.provenance.join('\n'),
  } : emptyFact());
  const openBullet = (bullet: ApprovedBullet) => setBulletForm({
    id: bullet.id, text: bullet.text, tags: bullet.tags.join(', '), factIds: bullet.factIds,
    personaIds: bullet.personaIds ?? [], wasApproved: bullet.state === 'approved',
  });
  const openPersona = (persona?: Persona) => {
    setPersonaSuggestionStatus('');
    setPersonaForm(persona ? {
      id: persona.id, name: persona.name, roleFamily: persona.roleFamily,
      seniority: persona.seniority ?? '', locationStrategy: persona.locationStrategy ?? '',
      compensationMinimum: persona.compensationMinimum === undefined ? '' : String(persona.compensationMinimum),
      queries: persona.queries.join('\n'), active: persona.active !== false,
    } : emptyPersona());
  };
  const openAnswer = (answer?: ApplicationAnswer) => setAnswerForm(answer ? {...answer} : emptyAnswer());

  return (
    <>
      <PageHeader
        eyebrow="Truth, structured"
        title="Build a career record richer than a résumé."
        description="Every fact keeps its source. Bullet wording stays behind explicit approval. Search directions and reusable answers remain under your control."
        actions={<div className="pd-detail-actions">
          <PrimaryButton leftSection={<Plus size={16} />} onClick={() => setEvidenceOpen(true)}>Add evidence</PrimaryButton>
          <SecondaryButton component="a" href="/onboarding">
            Career interview
          </SecondaryButton>
          {tab === 'facts' ? <SecondaryButton onClick={() => openFact()}>Add fact</SecondaryButton> : null}
          {tab === 'personas' ? <SecondaryButton onClick={() => openPersona()}>Add persona</SecondaryButton> : null}
          {tab === 'answers' ? <SecondaryButton onClick={() => openAnswer()}>Add answer</SecondaryButton> : null}
        </div>}
      />
      {notice ? <div className="pd-inline-status" role="status">
        <Check size={17} /><span>{notice}</span>
        <QuietButton aria-label="Dismiss status" onClick={() => setNotice('')}><X size={15} /></QuietButton>
      </div> : null}

      <Surface className="pd-source-resume-manager" id="source-resume-manager">
        <div className="pd-card-topline">
          <div>
            <span>Source resume</span>
            <h2>{activeResume?.name ?? 'No active source resume'}</h2>
          </div>
          {activeResume ? <strong>Active</strong> : null}
        </div>
        <p>
          A replacement becomes active only after secure upload, extraction, and provenance persistence succeed.
          Earlier versions remain available, and only dependent facts and generated documents reopen for review.
        </p>
        <div className="pd-detail-actions">
          <label>
            <span>Replacement resume</span>
            <input
              type="file"
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              onChange={(event) => setReplacementResume(event.currentTarget.files?.[0] ?? null)}
            />
          </label>
          <PrimaryButton
            leftSection={<FileUp size={16} aria-hidden="true" />}
            disabled={!replacementResume}
            loading={replaceResume.isPending}
            onClick={() => replacementResume && replaceResume.mutate(replacementResume)}
          >
            Replace source resume
          </PrimaryButton>
        </div>
        {priorResumes.length ? (
          <details>
            <summary>{priorResumes.length} previous source {priorResumes.length === 1 ? 'version' : 'versions'}</summary>
            <ul>{priorResumes.map((document) => <li key={document.id}>{document.name} · {formatRelativeDate(document.createdAt)}</li>)}</ul>
          </details>
        ) : null}
        {replaceResume.isError ? <p className="pd-form-error" role="alert">{errorMessage(replaceResume.error)}</p> : null}
      </Surface>

      <div className="pd-profile-tabs" role="tablist" aria-label="Career profile sections">
        {([
          ['facts', 'Career facts', Fingerprint, facts.data?.items.length ?? 0],
          ['bullets', 'Bullet library', FileCheck2, bullets.data?.items.length ?? 0],
          ['personas', 'Personas & queries', Tags, personas.data?.items.length ?? 0],
          ['answers', 'Application answers', MessageSquareText, answers.data?.items.length ?? 0],
        ] as const).map(([value, label, Icon, count]) => (
          <button key={value} type="button" role="tab" aria-selected={tab === value}
            className={tab === value ? 'is-active' : ''} onClick={() => { setTab(value); setQuery(''); }}>
            <Icon size={17} />{label}<span>{count}</span>
          </button>
        ))}
      </div>
      <div className="pd-profile-toolbar">
        <label className="pd-search-control pd-profile-search">
          <Search size={17} /><span className="pd-visually-hidden">Search {tab}</span>
          <input value={query} onChange={(event) => setQuery(event.currentTarget.value)} placeholder={'Search ' + tab} />
        </label>
        {tab === 'facts' || tab === 'bullets' ? <label className="pd-check-control">
          <input type="checkbox" checked={showArchived} onChange={(event) => setShowArchived(event.currentTarget.checked)} />
          Show archived
        </label> : null}
      </div>
      {active.isLoading ? <LoadingPanel label={'Loading ' + tab} /> : null}
      {active.isError ? <ErrorPanel error={active.error} /> : null}

      {tab === 'facts' && facts.data ? (
        filteredFacts.length ? <div className="pd-fact-list">{filteredFacts.map((fact) => (
          <Surface key={fact.id} className={'pd-fact-row state-' + fact.state + (fact.archived ? ' is-archived' : '')}>
            <div className="pd-fact-state" title={fact.state.replaceAll('_', ' ')}>
              {fact.state === 'inferred_unconfirmed' ? <ShieldQuestion size={19} /> : <BookOpenCheck size={19} />}
            </div>
            <div className="pd-fact-copy">
              <span>{fact.type} · {fact.state.replaceAll('_', ' ')}</span><h3>{fact.value}</h3>
              {fact.reviewRequired ? <strong>Review required after source resume replacement</strong> : null}
              <p>Source: {fact.sourceLabel} · confidence {Math.round(fact.confidence * 100)}% · updated {formatRelativeDate(fact.updatedAt)}</p>
              <details><summary>Inspect provenance</summary><ul>{fact.provenance.map((source) => <li key={source}>{source}</li>)}</ul></details>
            </div>
            <div className="pd-fact-actions">
              {fact.state === 'inferred_unconfirmed' || fact.reviewRequired ? <PrimaryButton onClick={() => updateFact.mutate({id: fact.id, input: {state: 'user_confirmed', ...(fact.reviewRequired ? {reviewRequired: false} : {})}})}>Confirm</PrimaryButton> : null}
              <QuietButton leftSection={<Pencil size={15} />} onClick={() => openFact(fact)}>Edit</QuietButton>
              {fact.state !== 'rejected' ? <QuietButton onClick={() => updateFact.mutate({id: fact.id, input: {state: 'rejected', ...(fact.reviewRequired ? {reviewRequired: false} : {})}})}>Reject</QuietButton> : null}
              <QuietButton aria-label={(fact.archived ? 'Restore' : 'Archive') + ' fact ' + fact.value}
                onClick={() => updateFact.mutate({id: fact.id, input: {archived: !fact.archived}})}>
                {fact.archived ? <RotateCcw size={16} /> : <Archive size={16} />}
              </QuietButton>
            </div>
          </Surface>
        ))}</div> : <EmptyPanel title="No facts match." description="Clear the search, show archived facts, or add a fact or evidence." />
      ) : null}

      {tab === 'bullets' && bullets.data ? (
        filteredBullets.length ? <div className="pd-bullet-list">{filteredBullets.map((bullet) => (
          <Surface key={bullet.id} className={'pd-bullet-row state-' + bullet.state}>
            <div className="pd-bullet-main">
              <div className="pd-card-topline"><span>Version {bullet.version}</span><strong>{bullet.state}</strong></div>
              <p>{bullet.text}</p>
              <div className="pd-tag-list">{bullet.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
              <details><summary>Linked evidence ({bullet.factIds.length})</summary><ul>
                {bullet.factIds.map((id) => <li key={id}>{facts.data?.items.find((fact) => fact.id === id)?.value ?? id}</li>)}
              </ul></details>
            </div>
            <div className="pd-bullet-actions">
              {bullet.state === 'draft' ? <PrimaryButton onClick={() => updateBullet.mutate({id: bullet.id, input: {state: 'approved'}})}>Approve exact wording</PrimaryButton> : null}
              {bullet.state === 'approved' ? <span className="pd-approved-stamp"><FileCheck2 size={16} />Resume-ready</span> : null}
              <QuietButton leftSection={<Pencil size={15} />} onClick={() => openBullet(bullet)}>Edit</QuietButton>
              {!['rejected', 'archived'].includes(bullet.state) ? <QuietButton onClick={() => updateBullet.mutate({id: bullet.id, input: {state: 'rejected'}})}>Reject</QuietButton> : null}
              <QuietButton aria-label={(bullet.state === 'archived' ? 'Restore' : 'Archive') + ' bullet'}
                onClick={() => updateBullet.mutate({id: bullet.id, input: {state: bullet.state === 'archived' ? 'draft' : 'archived'}})}>
                {bullet.state === 'archived' ? <RotateCcw size={16} /> : <Archive size={16} />}
              </QuietButton>
            </div>
          </Surface>
        ))}</div> : <EmptyPanel title="No bullet candidates match." description="Add evidence to generate draft bullets, or show archived bullets." />
      ) : null}

      {tab === 'personas' && personas.data ? (
        filteredPersonas.length ? <div className="pd-persona-grid">{filteredPersonas.map((persona) => (
          <Surface key={persona.id} className={'pd-persona-card' + (persona.active === false ? ' is-archived' : '')}>
            <div className="pd-card-topline">
              <span>{persona.active === false ? 'Paused' : persona.confirmed ? 'Confirmed search persona' : 'Confirmation needed'}</span>
              {persona.confirmed ? <Check size={16} /> : <CircleHelp size={16} />}
            </div>
            <h3>{persona.name}</h3><p>{persona.roleFamily}</p>
            <dl>
              <div><dt>Seniority</dt><dd>{persona.seniority || 'Flexible'}</dd></div>
              <div><dt>Location</dt><dd>{persona.locationStrategy || 'Not set'}</dd></div>
              <div><dt>Minimum</dt><dd>{persona.compensationMinimum === undefined ? 'Not set' : '$' + persona.compensationMinimum.toLocaleString()}</dd></div>
              <div><dt>Queries</dt><dd>{persona.queries.length} {persona.queryConfirmation === 'confirmed' ? 'confirmed' : 'draft'}</dd></div>
            </dl>
            <div className="pd-query-list"><strong>Search queries</strong>
              {persona.queries.length ? persona.queries.map((value) => <code key={value}>{value}</code>) : <span>Add a query before confirmation.</span>}
            </div>
            <div className="pd-card-actions">
              <SecondaryButton onClick={() => openPersona(persona)}>Edit persona</SecondaryButton>
              {!persona.confirmed && persona.queries.length ? <PrimaryButton loading={confirmPersona.isPending}
                onClick={() => confirmPersona.mutate(persona.id)}>Confirm search preferences</PrimaryButton> : null}
            </div>
          </Surface>
        ))}</div> : <EmptyPanel title="No personas match." description="Create distinct, honest search directions for the roles you would pursue."
          action={<PrimaryButton onClick={() => openPersona()}>Create a persona</PrimaryButton>} />
      ) : null}

      {tab === 'answers' && answers.data ? (
        filteredAnswers.length ? <div className="pd-answer-list">{filteredAnswers.map((answer) => (
          <Surface key={answer.id} className="pd-answer-row">
            <div className="pd-card-topline"><span>{answer.sensitivity.replaceAll('_', ' ')}</span><strong>{answer.reusePreference.replaceAll('_', ' ')}</strong></div>
            <h3>{answer.originalQuestion}</h3><p>{answer.answer}</p>
            <dl className="pd-answer-metadata">
              <div><dt>Meaning</dt><dd>{answer.normalizedQuestion}</dd></div>
              <div><dt>Provenance</dt><dd>{answer.provenance.replaceAll('_', ' ')}</dd></div>
              <div><dt>Updated</dt><dd>{formatRelativeDate(answer.updatedAt)}</dd></div>
              <div><dt>Source</dt><dd>{answer.sourceApplicationId || answer.sourcePageId || 'Profile entry'}</dd></div>
            </dl>
            {freshConfirmation(answer.sensitivity) ? <p className="pd-sensitive-note">This answer always requires fresh confirmation and is never reused silently.</p> : null}
            <div className="pd-card-actions"><SecondaryButton onClick={() => openAnswer(answer)}>Edit answer</SecondaryButton></div>
          </Surface>
        ))}</div> : <EmptyPanel title="No reusable answers match." description="Add exact answers to propose for semantically equivalent application questions."
          action={<PrimaryButton onClick={() => openAnswer()}>Add an answer</PrimaryButton>} />
      ) : null}

      <EvidenceCapture opened={evidenceOpen} onClose={() => setEvidenceOpen(false)} onSaved={async () => {
        await refresh('career-facts', 'career-bullets', 'documents');
      }} />

      <Modal opened={Boolean(factForm)} onClose={() => setFactForm(null)} title={factForm?.id ? 'Edit career fact' : 'Add a career fact'} centered>
        {factForm ? <form className="pd-modal-form" onSubmit={(event: FormEvent) => { event.preventDefault(); saveFact.mutate(factForm); }}>
          <div className="pd-editor-grid">
            <TextInput label="Fact type" required value={factForm.type} onChange={(event) => setFactForm({...factForm, type: event.currentTarget.value})} />
            <Select label="Confirmation state" value={factForm.state} data={[
              ...(factForm.state === 'source_backed'
                ? [{value: 'source_backed', label: 'Source-backed evidence', disabled: true}]
                : []),
              {value: 'user_confirmed', label: 'Confirmed by me'},
              {value: 'inferred_unconfirmed', label: 'Inferred — needs confirmation'}, {value: 'rejected', label: 'Rejected'},
            ]} onChange={(value) => value && setFactForm({...factForm, state: value as CareerFact['state']})} />
          </div>
          {factForm.state === 'source_backed' ? (
            <p className="pd-sensitive-note">Changes you make will be recorded as confirmed by you.</p>
          ) : null}
          <Textarea label="Exact fact" required autosize minRows={3} value={factForm.value} onChange={(event) => setFactForm({...factForm, value: event.currentTarget.value})} />
          <div className="pd-editor-grid">
            <TextInput label="Source label" required value={factForm.sourceLabel} onChange={(event) => setFactForm({...factForm, sourceLabel: event.currentTarget.value})} />
            <TextInput label="Confidence (0–1)" type="number" min={0} max={1} step={0.01} required value={factForm.confidence} onChange={(event) => setFactForm({...factForm, confidence: event.currentTarget.value})} />
          </div>
          <Textarea label="Provenance — one source per line" required autosize minRows={2} value={factForm.provenance} onChange={(event) => setFactForm({...factForm, provenance: event.currentTarget.value})} />
          <label className="pd-check-control"><input type="checkbox" checked={factForm.archived} onChange={(event) => setFactForm({...factForm, archived: event.currentTarget.checked})} />Archive this fact</label>
          {saveFact.isError ? <p className="pd-form-error" role="alert">{errorMessage(saveFact.error)}</p> : null}
          <div className="pd-form-actions"><SecondaryButton type="button" onClick={() => setFactForm(null)}>Cancel</SecondaryButton><PrimaryButton type="submit" loading={saveFact.isPending}>Save fact</PrimaryButton></div>
        </form> : null}
      </Modal>

      <Modal opened={Boolean(bulletForm)} onClose={() => setBulletForm(null)} title="Edit bullet wording" centered size="lg">
        {bulletForm ? <form className="pd-modal-form" onSubmit={(event: FormEvent) => { event.preventDefault(); saveBullet.mutate(bulletForm); }}>
          {bulletForm.wasApproved ? <p className="pd-sensitive-note">The approved version is immutable. Saving creates a new draft for separate approval.</p> : null}
          <Textarea label="Exact bullet wording" required autosize minRows={4} value={bulletForm.text} onChange={(event) => setBulletForm({...bulletForm, text: event.currentTarget.value})} />
          <TextInput label="Tags — comma separated" value={bulletForm.tags} onChange={(event) => setBulletForm({...bulletForm, tags: event.currentTarget.value})} />
          <fieldset className="pd-choice-fieldset"><legend>Linked source facts</legend>
            {(facts.data?.items ?? []).filter((fact) => !fact.archived && fact.state !== 'rejected').map((fact) => <label key={fact.id} className="pd-check-control">
              <input type="checkbox" checked={bulletForm.factIds.includes(fact.id)} onChange={(event) => setBulletForm({
                ...bulletForm, factIds: event.currentTarget.checked ? [...bulletForm.factIds, fact.id] : bulletForm.factIds.filter((id) => id !== fact.id),
              })} />{fact.value}
            </label>)}
          </fieldset>
          <fieldset className="pd-choice-fieldset"><legend>Applicable personas</legend>
            {(personas.data?.items ?? []).map((persona) => <label key={persona.id} className="pd-check-control">
              <input type="checkbox" checked={bulletForm.personaIds.includes(persona.id)} onChange={(event) => setBulletForm({
                ...bulletForm, personaIds: event.currentTarget.checked ? [...bulletForm.personaIds, persona.id] : bulletForm.personaIds.filter((id) => id !== persona.id),
              })} />{persona.name}
            </label>)}
          </fieldset>
          {!bulletForm.factIds.length ? <p className="pd-form-error">At least one source fact is required.</p> : null}
          {saveBullet.isError ? <p className="pd-form-error" role="alert">{errorMessage(saveBullet.error)}</p> : null}
          <div className="pd-form-actions"><SecondaryButton type="button" onClick={() => setBulletForm(null)}>Cancel</SecondaryButton><PrimaryButton type="submit" loading={saveBullet.isPending} disabled={!bulletForm.factIds.length}>Save bullet wording</PrimaryButton></div>
        </form> : null}
      </Modal>

      <Modal opened={Boolean(personaForm)} onClose={() => setPersonaForm(null)} title={personaForm?.id ? 'Edit career persona' : 'Create a career persona'} centered size="lg">
        {personaForm ? <form className="pd-modal-form" onSubmit={(event: FormEvent) => { event.preventDefault(); savePersona.mutate(personaForm); }}>
          <div className="pd-editor-grid">
            <TextInput label="Persona name" required value={personaForm.name} onChange={(event) => setPersonaForm({...personaForm, name: event.currentTarget.value})} />
            <TextInput label="Role family" required value={personaForm.roleFamily} onChange={(event) => setPersonaForm({...personaForm, roleFamily: event.currentTarget.value})} />
            <TextInput label="Seniority direction" value={personaForm.seniority} onChange={(event) => setPersonaForm({...personaForm, seniority: event.currentTarget.value})} />
            <TextInput label="Minimum compensation" type="number" min={0} max={10000000} value={personaForm.compensationMinimum} onChange={(event) => setPersonaForm({...personaForm, compensationMinimum: event.currentTarget.value})} />
          </div>
          <Textarea label="Location strategy" autosize minRows={2} value={personaForm.locationStrategy} onChange={(event) => setPersonaForm({...personaForm, locationStrategy: event.currentTarget.value})} />
          <Textarea label="Search queries — one per line" autosize minRows={3} value={personaForm.queries} onChange={(event) => setPersonaForm({...personaForm, queries: event.currentTarget.value})} />
          <div className="pd-form-actions pd-form-actions--between">
            <SecondaryButton
              type="button"
              leftSection={<Sparkles size={15} aria-hidden="true" />}
              loading={suggestPersonaQueries.isPending}
              disabled={!personaForm.roleFamily.trim() || bootstrap.data?.capabilities?.ai.usable === false}
              title={bootstrap.data?.capabilities?.ai.usable === false ? bootstrap.data.capabilities.ai.message : undefined}
              onClick={() => suggestPersonaQueries.mutate(personaForm)}
            >
              Suggest from confirmed profile
            </SecondaryButton>
          </div>
          {bootstrap.data?.capabilities?.ai.usable === false ? (
            <p className="pd-form-help" role="status">{bootstrap.data.capabilities.ai.message}</p>
          ) : null}
          {personaSuggestionStatus ? <p className="pd-form-help" role="status">{personaSuggestionStatus}</p> : null}
          <p className="pd-form-help">Suggestions are drafts. Material changes return this persona to draft; save it, then confirm the exact queries from its card.</p>
          <label className="pd-check-control"><input type="checkbox" checked={personaForm.active} onChange={(event) => setPersonaForm({...personaForm, active: event.currentTarget.checked})} />Active for discovery</label>
          {savePersona.isError ? <p className="pd-form-error" role="alert">{errorMessage(savePersona.error)}</p> : null}
          <div className="pd-form-actions"><SecondaryButton type="button" onClick={() => setPersonaForm(null)}>Cancel</SecondaryButton><PrimaryButton type="submit" loading={savePersona.isPending}>Save draft persona</PrimaryButton></div>
        </form> : null}
      </Modal>

      <Modal opened={Boolean(answerForm)} onClose={() => setAnswerForm(null)} title={answerForm?.id ? 'Edit application answer' : 'Add an application answer'} centered size="lg">
        {answerForm ? <form className="pd-modal-form" onSubmit={(event: FormEvent) => { event.preventDefault(); saveAnswer.mutate(answerForm); }}>
          <TextInput label="Original question wording" required value={answerForm.originalQuestion} onChange={(event) => {
            const originalQuestion = event.currentTarget.value;
            setAnswerForm({...answerForm, originalQuestion, normalizedQuestion: answerForm.normalizedQuestion || originalQuestion.toLowerCase()});
          }} />
          <TextInput label="Normalized meaning" required value={answerForm.normalizedQuestion} onChange={(event) => setAnswerForm({...answerForm, normalizedQuestion: event.currentTarget.value})} />
          <Textarea label="Exact answer" required autosize minRows={4} value={answerForm.answer} onChange={(event) => setAnswerForm({...answerForm, answer: event.currentTarget.value})} />
          <div className="pd-editor-grid">
            <Select label="Sensitivity" value={answerForm.sensitivity} data={[
              'none', 'demographic', 'disability', 'veteran', 'criminal_history',
              'work_authorization', 'attestation', 'signature', 'employer_consent',
            ].map((value) => ({value, label: value.replaceAll('_', ' ')}))}
              onChange={(value) => value && setAnswerForm({
                ...answerForm, sensitivity: value as ApplicationAnswer['sensitivity'],
                reusePreference: freshConfirmation(value as ApplicationAnswer['sensitivity']) ? 'never' : answerForm.reusePreference,
              })} />
            <Select label="Reuse preference" value={answerForm.reusePreference} disabled={freshConfirmation(answerForm.sensitivity)}
              data={[{value: 'always_propose', label: 'Always propose'}, {value: 'ask_each_time', label: 'Ask each time'}, {value: 'never', label: 'Never reuse'}]}
              onChange={(value) => value && setAnswerForm({...answerForm, reusePreference: value as ApplicationAnswer['reusePreference']})} />
          </div>
          <p className="pd-form-help">Provenance: {answerForm.provenance.replaceAll('_', ' ')}. Sensitive answers can be reused only when they came directly from you.</p>
          {freshConfirmation(answerForm.sensitivity) ? <p className="pd-sensitive-note">Attestations, signatures, and employer consent are never reusable and require fresh confirmation.</p> : null}
          {saveAnswer.isError ? <p className="pd-form-error" role="alert">{errorMessage(saveAnswer.error)}</p> : null}
          <div className="pd-form-actions"><SecondaryButton type="button" onClick={() => setAnswerForm(null)}>Cancel</SecondaryButton><PrimaryButton type="submit" loading={saveAnswer.isPending}>Save exact answer</PrimaryButton></div>
        </form> : null}
      </Modal>
    </>
  );
};
